import { useEffect, useState } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";
import { AlertTriangle, RefreshCw, ArrowLeft } from "@/shared/icons";
import { Button } from "@/shared/components/Button";
import { Card } from "@/shared/components/Card";

const CHUNK_LOAD_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "ChunkLoadError",
];

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    (err as { message?: string })?.message ??
    (typeof err === "string" ? err : "") ??
    "";
  return CHUNK_LOAD_PATTERNS.some((p) => msg.includes(p));
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [online, setOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const chunkError = isChunkLoadError(error);
  const routeResp = isRouteErrorResponse(error) ? error : null;

  useEffect(() => {
    const setOn = () => setOnline(true);
    const setOff = () => setOnline(false);
    window.addEventListener("online", setOn);
    window.addEventListener("offline", setOff);
    return () => {
      window.removeEventListener("online", setOn);
      window.removeEventListener("offline", setOff);
    };
  }, []);

  // Auto-recover: when chunk failed and connectivity returns, hard-reload to fetch fresh assets.
  useEffect(() => {
    if (!chunkError || !online) return;
    const t = setTimeout(() => window.location.reload(), 400);
    return () => clearTimeout(t);
  }, [chunkError, online]);

  let title: string;
  let description: string;

  if (!online) {
    title = "You appear to be offline";
    description = "Check your internet connection. This page will reload automatically once you're back online.";
  } else if (chunkError) {
    title = "Reloading to get the latest version";
    description = "A newer build is available. Refreshing now...";
  } else if (routeResp?.status === 404) {
    title = "Page not found";
    description = "The page you're looking for doesn't exist or has been moved.";
  } else if (routeResp?.status === 403) {
    title = "Access denied";
    description = "You don't have permission to view this page.";
  } else {
    title = "Something went wrong";
    description = "An unexpected error occurred. Try reloading the page.";
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card padding="lg" className="w-full max-w-md">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--bg-sunken)] text-[var(--ink-3)]">
            <AlertTriangle size={22} />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold text-[var(--ink-1)]">{title}</h2>
            <p className="mt-1 text-[13px] text-[var(--ink-3)]">{description}</p>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft size={14} /> Back
            </Button>
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
              <RefreshCw size={14} /> Reload
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
